import { Button, Form, Input, InputNumber, Select, Switch } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { isEqual } from "lodash";

import { promptCardService } from "./prompt-card-service";
import { useCustomToolStore } from "../../store/custom-tool-store";
import { useAlertStore } from "../../store/alert-store";
import { useExceptionHandler } from "../../hooks/useExceptionHandler";
import { SpinnerLoader } from "../../components/widgets/spinner-loader/SpinnerLoader";

const DEFAULT_VALUES = {
  headers: [],
  start_page: 0,
  end_page: 0,
  document_type: ["default"],
  compress_double_space: false,
  page_delimiter: "<<<",
  use_form_feed: false,
  disable_span_search: false,
  is_directory_mode: false,
  page_filter_strategy: ["contiguous"],
};

function TableExtractionSettings({
  open,
  setOpen,
  promptId,
  enforceType,
  setAllTableSettings,
}) {
  const [form] = Form.useForm();
  const [initialSetupCompleted, setInitialSetupCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormSubmitLoading, setIsFormSubmitLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [tableSettingsId, setTableSettingsId] = useState(false);
  const [docTypeDropdownItems, setDocTypeDropdownItems] = useState([]);
  const [filterStrategyDropdownItems, setFilterStrategyDropdownItems] =
    useState([]);
  const promptCardApiService = promptCardService();
  const { details, dropdownItems } = useCustomToolStore();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();

  useEffect(() => {
    if (open && !initialSetupCompleted) {
      setInitialSetupCompleted(true);
      getExistingTableSettingsDetails();
      handleDocTypeDropdownItems();
      handleFilterStrategyDropdownItems();
    }
  }, [open]);

  const handleDocTypeDropdownItems = () => {
    const docTypes = Object.entries(dropdownItems?.table_document_type).map(
      ([value, label]) => ({
        value,
        label,
      })
    );
    setDocTypeDropdownItems(docTypes);
  };

  const handleFilterStrategyDropdownItems = () => {
    const filterStrategy = Object.entries(
      dropdownItems?.table_page_filter_strategy
    ).map(([value, label]) => ({
      value,
      label,
    }));
    setFilterStrategyDropdownItems(filterStrategy);
  };

  const getExistingTableSettingsDetails = () => {
    promptCardApiService
      .getTableSettings(promptId, enforceType)
      .then((res) => {
        const data = res?.data[0] || {};
        const tableSettings = setExistingTableSettings(data);
        setInitialValues(tableSettings);
        setTableSettingsId(data?.id);
      })
      .catch((err) => {
        setAlertDetails(handleException(err));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const setExistingTableSettings = (data) => {
    return {
      headers: data?.headers || DEFAULT_VALUES?.headers,
      start_page: data?.start_page || DEFAULT_VALUES?.start_page,
      end_page: data?.end_page || DEFAULT_VALUES?.end_page,
      document_type:
        (data?.document_type && [data?.document_type]) ||
        DEFAULT_VALUES?.document_type,
      compress_double_space:
        data?.compress_double_space || DEFAULT_VALUES?.compress_double_space,
      page_delimiter: data?.page_delimiter || DEFAULT_VALUES?.page_delimiter,
      use_form_feed: data?.use_form_feed || DEFAULT_VALUES?.use_form_feed,
      disable_span_search:
        data?.disable_span_search || DEFAULT_VALUES?.disable_span_search,
      is_directory_mode:
        data?.is_directory_mode || DEFAULT_VALUES?.is_directory_mode,
      page_filter_strategy:
        data?.page_filter_strategy || DEFAULT_VALUES?.page_filter_strategy,
    };
  };

  const getChangedValues = (changedValues, allValues) => {
    return Object.keys(changedValues).reduce((acc, key) => {
      if (
        Array.isArray(changedValues[key]) &&
        !isEqual(changedValues[key].sort(), allValues[key].sort())
      ) {
        acc[key] = changedValues[key];
      } else if (
        typeof changedValues[key] !== "object" &&
        changedValues[key] !== allValues[key]
      ) {
        acc[key] = changedValues[key]; // Only add to acc if the value has changed
      }
      return acc;
    }, {});
  };

  const onFinish = (values) => {
    let body = {
      ...values,
      prompt_id: promptId,
      tool_id: details?.tool_id,
      enforce_type: enforceType,
    };
    setIsFormSubmitLoading(true);
    let funcToStoreTableSettings;
    if (tableSettingsId) {
      const changedValues = getChangedValues(values, initialValues);
      body = changedValues;
      // PATCH if tableSettingsId exists
      funcToStoreTableSettings = promptCardApiService.patchTableSettings;
    } else {
      // POST if it's a new form
      funcToStoreTableSettings = promptCardApiService.postTableSettings;
    }
    if (body?.document_type) body.document_type = body?.document_type[0];
    if (body?.page_filter_strategy)
      body.page_filter_strategy = body?.page_filter_strategy[0];

    funcToStoreTableSettings(body, tableSettingsId)
      .then((res) => {
        const data = res?.data;
        setTableSettingsId(data?.id);
        setOpen(false);
        setAlertDetails({
          type: "success",
          content: tableSettingsId
            ? "Successfully updated the table extraction settings"
            : "Successfully created new table extraction settings",
        });
        setAllTableSettings((prevItems) => {
          const index = prevItems.findIndex((item) => item.id === data.id);
          if (index !== -1) {
            // Update existing item
            const updated = [...prevItems];
            updated[index] = data;
            return updated;
          } else {
            // Add new item
            return [...prevItems, data];
          }
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err));
      })
      .finally(() => {
        setIsFormSubmitLoading(false);
      });
  };

  if (isLoading) {
    return <SpinnerLoader />;
  }

  return (
    <Form
      form={form}
      name="documentForm"
      onFinish={onFinish}
      layout="vertical"
      initialValues={initialValues}
    >
      <Form.Item
        label="Headers"
        name="headers"
        tooltip={{
          title: "Column headers that has to be extracted.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Name, City, etc."
        />
      </Form.Item>
      <Form.Item label="Start Page" name="start_page">
        <InputNumber min={0} className="width-100" />
      </Form.Item>
      <Form.Item
        label="End Page"
        name="end_page"
        extra={
          "Please ensure that a maximum of 10 pages will be extracted in Prompt studio. However, the entered settings will be reflected while exporting as tool or deploying as API."
        }
      >
        <InputNumber min={0} className="width-100" />
      </Form.Item>
      <Form.Item
        label="Document Type"
        name="document_type"
        initialValue="default"
        tooltip={{
          title: "Type of the table doc. Eg. Bank Statement, Resume etc.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Select
          placeholder="Select or enter document type"
          mode="tags"
          options={docTypeDropdownItems}
          maxCount={1}
        />
      </Form.Item>
      <Form.Item
        label="Page Delimiter"
        name="page_delimiter"
        tooltip={{
          title: "Delimiter for page splitting.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Compress Double Space"
        name="compress_double_space"
        valuePropName="checked"
        tooltip={{
          title: "Compresses extra double space in each cell of the table.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Disable Span Search"
        name="disable_span_search"
        valuePropName="checked"
        tooltip={{
          title: "Disables searching of table span in the document.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Use Form Feed"
        name="use_form_feed"
        valuePropName="checked"
        tooltip={{
          title: "Override page delimiter with form feed character.",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Page Filter Strategy"
        name="page_filter_strategy"
        initialValue="contiguous"
        tooltip={{
          title: (
            <>
              <p>Choose how to detect the end of the table:</p>
              <ul>
                <li>
                  <strong>Contiguous Pages Strategy</strong>: Checks if a table
                  continues across two sequential pages based on column
                  structure, even if headers are missing. Handles overflowed or
                  repeating-table formats.
                </li>
                <li>
                  <strong>Header-based Strategy</strong>: Checks if a page
                  contains a table matching the header structure. Useful when
                  headers might not be repeated on every page.
                </li>
              </ul>
            </>
          ),
          icon: <InfoCircleOutlined />,
        }}
      >
        <Select
          placeholder="Select or enter document type"
          mode="tags"
          options={filterStrategyDropdownItems}
          maxCount={1}
        />
      </Form.Item>
      <Form.Item>
        <div className="display-flex-right">
          <Button
            type="primary"
            htmlType="submit"
            loading={isFormSubmitLoading}
          >
            {!tableSettingsId ? "Submit" : "Update"}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
}

TableExtractionSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  promptId: PropTypes.string.isRequired,
  enforceType: PropTypes.string.isRequired,
  setAllTableSettings: PropTypes.func.isRequired,
};

export { TableExtractionSettings };
